import { Component, ElementRef, HostListener, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { Store } from '@ngrx/store';
import { Router } from '@angular/router';
import { Subject, takeUntil } from 'rxjs';
import { Popover } from 'primeng/popover';
import { ProjectAccessRoles } from '@completion/models';
import { State } from '@completion/reducers';
import { getHeatTraceConfigured, getRole } from '@completion/selectors';
import { UserAccessService } from '../../core/services/user-access.service';
import { KEYS } from '../../shared/constants/keyboard-keys';
import { Feature, FeatureFlagService } from '../../core/services/feature-flag.service';

export interface AppItem {
  label: string;
  title?: string;
  icon: string;
  route?: string;
  url?: string;
  disabled?: boolean;
  /** Visible only when the user has the AAD ADMIN role. */
  adminOnly?: boolean;
  /** Visible for AAD ADMIN **or** project ADMIN. */
  projectSetup?: boolean;
  /** Visible only when the user has the AAD USER_RW role. */
  requiresUserRW?: boolean;
  /** Hidden for users with the VENDOR project role. */
  requiresNonVendor?: boolean;
  /** Visible only if user has Supplier-module access. */
  requiresSupplierAccess?: boolean;
  /** Visible only when the named feature is enabled. */
  requiresFeature?: Feature;
  /** Visible only if the active project has Heat Trace configured. */
  requiresHeatTraceConfigured?: boolean;
  /** Optional SVG path for the app icon. */
  svgPath?: string;
}

@Component({
  selector: 'app-launcher',
  templateUrl: './app-launcher.component.html',
  styleUrls: ['./app-launcher.component.scss'],
  standalone: false,
})
export class AppLauncherComponent implements OnInit, OnDestroy {
  isOpen = false;
  isAdmin = false;
  isUserRW = false;
  isProjectAdmin = false;
  isVendor = false;
  canAccessSupplierModule = false;
  heatTraceConfigured: boolean | null = null;
  private focusAfterOpen = false;
  private readonly destroy$ = new Subject<void>();

  @ViewChild('launcherPopover') private launcherPopover?: Popover;
  @ViewChild('launcherTrigger', { read: ElementRef }) private launcherTrigger?: ElementRef<HTMLElement>;
  @ViewChild('launcherGrid') private launcherGrid?: ElementRef<HTMLElement>;

  private readonly allModules: AppItem[] = [
    { label: 'Reports', icon: 'pi pi-chart-bar', route: '/reports', requiresUserRW: true, requiresNonVendor: true },
    { label: 'Query Module', icon: 'pi pi-search', route: '/semantic-search', requiresUserRW: true, requiresNonVendor: true },
    { label: 'RFCC Reports', icon: 'pi pi-check-circle', route: '/rfcc-reports', requiresNonVendor: true },
    { label: 'Punch Report', icon: 'pi pi-list', route: '/punch-report', requiresUserRW: true, requiresNonVendor: true },
    { label: 'Supplier', icon: 'pi pi-building', route: '/supplier', requiresUserRW: true, requiresNonVendor: true },
    { label: 'Bulk', icon: 'pi pi-clone', route: '/batch', requiresUserRW: true, requiresNonVendor: true },
    { label: 'Project Overview', icon: 'pi pi-sitemap', route: '/project-overview', requiresUserRW: true, requiresNonVendor: true },
    {
      label: 'Heat Tracing',
      icon: 'pi pi-sun',
      route: '/heat-trace',
      requiresUserRW: true,
      requiresNonVendor: true,
      requiresHeatTraceConfigured: true,
    },
    {
      label: 'PPFP',
      title: 'Procurement Package Follow up Plan',
      icon: 'pi pi-warehouse',
      route: '/ppfp',
      requiresUserRW: true,
      requiresNonVendor: true,
      requiresFeature: 'ppfp',
      svgPath:
        'M19 3h-4.18C14.4 1.84 13.3 1 12 1c-1.3 0-2.4.84-2.82 2H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zm-7 0c.55 0 1 .45 1 1s-.45 1-1 1-1-.45-1-1 .45-1 1-1zm2 14H7v-2h7v2zm3-4H7v-2h10v2zm0-4H7V7h10v2z',
    },
  ];

  private readonly allAdminModules: AppItem[] = [
    { label: 'Project Setup', icon: 'pi pi-cog', route: '/project-setup', projectSetup: true },
    { label: 'Admin', icon: 'pi pi-shield', route: '/admin', adminOnly: true },
  ];

  // TODO: Move sibling apps configuration to the database
  // private readonly allSiblingApps: AppItem[] = [
  //   { label: 'DWO – Digital Work Order', icon: 'assignment', url: 'https://dwo.akersolutions.com' },
  // ];

  visibleModules: AppItem[] = [];
  visibleAdminModules: AppItem[] = [];
  visibleSiblingApps: AppItem[] = [];

  constructor(
    private readonly router: Router,
    private readonly store: Store<State>,
    private readonly userAccess: UserAccessService,
    private readonly featureFlag: FeatureFlagService,
  ) {}

  ngOnInit(): void {
    this.userAccess.isGlobalAdmin$.pipe(takeUntil(this.destroy$)).subscribe((isAdmin) => {
      this.isAdmin = isAdmin;
      this.rebuildVisibleModules();
    });

    this.userAccess.hasUserRW$.pipe(takeUntil(this.destroy$)).subscribe((isUserRW) => {
      this.isUserRW = isUserRW;
      this.rebuildVisibleModules();
    });

    this.userAccess.isProjectAdmin$.pipe(takeUntil(this.destroy$)).subscribe((isProjectAdmin) => {
      this.isProjectAdmin = isProjectAdmin;
      this.rebuildVisibleModules();
    });

    this.userAccess.canAccessSupplierModule$.pipe(takeUntil(this.destroy$)).subscribe((canAccessSupplierModule) => {
      this.canAccessSupplierModule = canAccessSupplierModule;
      this.rebuildVisibleModules();
    });

    this.store
      .select(getRole)
      .pipe(takeUntil(this.destroy$))
      .subscribe((role) => {
        this.isVendor = role === ProjectAccessRoles.VENDOR;
        this.rebuildVisibleModules();
      });

    this.store
      .select(getHeatTraceConfigured)
      .pipe(takeUntil(this.destroy$))
      .subscribe((heatTraceConfigured) => {
        this.heatTraceConfigured = heatTraceConfigured;
        this.rebuildVisibleModules();
      });

    this.rebuildVisibleModules();
  }

  private rebuildVisibleModules(): void {
    const filterFn = (m: AppItem) => {
      if (m.adminOnly && !this.isAdmin) return false;
      if (m.projectSetup && !this.isAdmin && !this.isProjectAdmin) return false;
      if (m.requiresUserRW && !this.isUserRW) return false;
      if (m.requiresNonVendor && this.isVendor) return false;
      if (m.requiresSupplierAccess && !this.canAccessSupplierModule) return false;
      return true;
    };

    this.visibleModules = this.allModules.filter(filterFn).map((m) => {
      if (
        (m.requiresHeatTraceConfigured && this.heatTraceConfigured !== true) ||
        (m.requiresFeature && !this.featureFlag.isEnabled(m.requiresFeature))
      ) {
        return { ...m, disabled: true };
      }

      return m;
    });
    this.visibleAdminModules = this.allAdminModules.filter(filterFn).map((m) => {
      if (m.requiresFeature && !this.featureFlag.isEnabled(m.requiresFeature)) {
        return { ...m, disabled: true };
      }

      return m;
    });
    // this.visibleSiblingApps = this.allSiblingApps.filter((m) => !m.adminOnly || this.isAdmin);
  }

  onPopoverShow(): void {
    this.isOpen = true;

    if (this.focusAfterOpen) {
      setTimeout(() => {
        this.getLauncherItems()[0]?.focus();
        this.focusAfterOpen = false;
      });
    }
  }

  onPopoverHide(): void {
    this.isOpen = false;
  }

  navigate(item: AppItem): void {
    if (item.disabled) return;
    if (item.url) {
      window.open(item.url, '_blank');
    } else if (item.route) {
      this.router.navigate([item.route]);
    }
    this.launcherPopover?.hide();
  }

  @HostListener('document:keydown', ['$event'])
  onKeydown(event: KeyboardEvent): void {
    const key = event.key.toLowerCase();

    // Open launcher with 'a' or 'o' when no input is focused
    if (key === KEYS.A || key === KEYS.O) {
      const tag = (event.target as HTMLElement)?.tagName?.toLowerCase();
      if (tag === 'input' || tag === 'textarea' || tag === 'select' || (event.target as HTMLElement)?.isContentEditable) return;
      event.preventDefault();
      if (!this.isOpen) {
        this.focusAfterOpen = true;
        this.launcherTrigger?.nativeElement.click();
      }
      return;
    }

    // Close on Escape
    if (key === KEYS.ESCAPE && this.isOpen) {
      this.launcherPopover?.hide();
      this.launcherTrigger?.nativeElement.focus();
      return;
    }

    // Arrow / Tab navigation within the panel
    if (!this.isOpen) return;
    if (key !== KEYS.ARROW_UP && key !== KEYS.ARROW_DOWN && key !== KEYS.ARROW_LEFT && key !== KEYS.ARROW_RIGHT && key !== KEYS.TAB) return;

    const items = this.getLauncherItems();
    if (!items.length) return;

    const currentIndex = items.indexOf(document.activeElement as HTMLElement);
    const cols = 3; // items per row in the grid
    event.preventDefault();

    let next: number;
    if (key === KEYS.ARROW_RIGHT || (key === KEYS.TAB && !event.shiftKey)) {
      next = currentIndex < 0 ? 0 : (currentIndex + 1) % items.length;
    } else if (key === KEYS.ARROW_LEFT || (key === KEYS.TAB && event.shiftKey)) {
      next = currentIndex <= 0 ? items.length - 1 : currentIndex - 1;
    } else if (key === KEYS.ARROW_DOWN) {
      next = currentIndex < 0 ? 0 : currentIndex + cols < items.length ? currentIndex + cols : (currentIndex + 1) % items.length;
    } else {
      // arrowup
      next =
        currentIndex < 0
          ? items.length - 1
          : currentIndex - cols >= 0
            ? currentIndex - cols
            : (currentIndex - 1 + items.length) % items.length;
    }
    items[next]?.focus();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private getLauncherItems(): HTMLElement[] {
    return this.launcherGrid
      ? Array.from(this.launcherGrid.nativeElement.querySelectorAll<HTMLElement>('.launcher-item:not(:disabled)'))
      : [];
  }
}
